// Admin → Bulk Import. Three flows on one page:
//   1. Accounts (insert-only) — duplicates rejected, must be resolved manually.
//   2. Testers (insert-only) — duplicates by short_name (person) /
//      short_business_name (business) rejected.
//   3. Tiller Links (upsert) — relinks/initializes tiller_account_id.
// Each flow has a "Download Template" button + a file picker. Validation is
// all-or-nothing on the server: the request returns 422 with a row-by-row
// error list if anything is off.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  ApiError,
  adminImports,
  type AccountImportResult,
  type ImportError,
  type TesterImportResult,
  type TillerLinkImportResult,
} from '@/lib/api';

export function AdminImportsPage() {
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-slate-900">Bulk Import</h1>
      <p className="text-sm text-slate-600">
        Admin-only initial-port helpers. Account and Tester imports are
        insert-only — duplicates must be resolved manually before importing.
        Tiller link imports upsert existing accounts (links new ones, relinks
        linked ones).
      </p>
      <BulkImportCard
        title="Import Accounts (insert-only)"
        downloadTemplate={adminImports.downloadAccountsTemplate}
        importFn={adminImports.importAccounts}
        successLabel={(r) => `${r.created} account${r.created === 1 ? '' : 's'} created.`}
      />
      <BulkImportCard
        title="Import Testers (insert-only)"
        downloadTemplate={adminImports.downloadTestersTemplate}
        importFn={adminImports.importTesters}
        successLabel={(r) => `${r.created} tester${r.created === 1 ? '' : 's'} created.`}
      />
      <BulkImportCard
        title="Import Tiller Links (upsert)"
        downloadTemplate={adminImports.downloadTillerLinksTemplate}
        importFn={adminImports.importTillerLinks}
        successLabel={(r) =>
          `${r.linked} Tiller link${r.linked === 1 ? '' : 's'} applied.`
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared card component — drives all three importers off a parameterized
// download + import callback pair.
// ---------------------------------------------------------------------------

type ImportResult = AccountImportResult | TesterImportResult | TillerLinkImportResult;

function BulkImportCard<R extends ImportResult>({
  title,
  downloadTemplate,
  importFn,
  successLabel,
}: {
  title: string;
  downloadTemplate: () => Promise<void>;
  importFn: (rows: Record<string, unknown>[]) => Promise<R>;
  successLabel: (r: R) => string;
}) {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [parseError, setParseError] = useState<string | null>(null);

  const importMutation = useMutation<R, ApiError, Record<string, unknown>[]>({
    mutationFn: (data) => importFn(data),
  });
  const downloadMutation = useMutation({ mutationFn: () => downloadTemplate() });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => downloadMutation.mutate()}
            disabled={downloadMutation.isPending}
          >
            {downloadMutation.isPending ? 'Downloading…' : 'Download Template'}
          </Button>
          {downloadMutation.error && (
            <span className="text-xs text-red-600">
              {(downloadMutation.error as Error).message}
            </span>
          )}
        </div>

        <FilePicker
          accept=".xlsx,.xls"
          onPick={async (file) => {
            try {
              const buf = await file.arrayBuffer();
              const wb = XLSX.read(buf, { type: 'array', cellDates: false });
              const sheet = wb.Sheets[wb.SheetNames[0]];
              if (!sheet) throw new Error('workbook has no sheets');
              const parsed = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
                defval: '',
                raw: false,
              });
              setRows(parsed);
              setFileName(file.name);
              setParseError(null);
              importMutation.reset();
            } catch (err) {
              setParseError((err as Error).message);
              setRows([]);
              setFileName('');
            }
          }}
        />

        {fileName && (
          <div className="text-sm text-slate-600">
            Loaded <span className="font-mono">{fileName}</span> — {rows.length} row
            {rows.length === 1 ? '' : 's'}.
          </div>
        )}
        {parseError && <div className="text-sm text-red-600">Parse error: {parseError}</div>}

        <div>
          <Button
            onClick={() => importMutation.mutate(rows)}
            disabled={rows.length === 0 || importMutation.isPending}
          >
            {importMutation.isPending ? 'Validating & importing…' : 'Validate & Commit'}
          </Button>
        </div>

        <ImportResultPanel
          result={importMutation.data}
          error={importMutation.error}
          successLabel={successLabel}
        />
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function FilePicker({
  accept,
  onPick,
}: {
  accept: string;
  onPick: (file: File) => Promise<void> | void;
}) {
  return (
    <label className="block text-sm">
      <span className="block text-slate-600">Choose file (.xlsx)</span>
      <Input
        type="file"
        accept={accept}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onPick(f);
        }}
      />
    </label>
  );
}

function ImportResultPanel<T extends { errors: ImportError[] }>({
  result,
  error,
  successLabel,
}: {
  result: T | undefined;
  error: ApiError | null;
  successLabel: (r: T) => string;
}) {
  const errorRows: ImportError[] = error ? extractErrorRows(error) ?? [] : [];
  const success = result && result.errors.length === 0;

  if (!success && !error) return null;

  return (
    <div className="rounded border border-slate-200 p-3 text-sm">
      {success && (
        <div className="text-emerald-700 font-medium">{successLabel(result!)}</div>
      )}
      {error && (
        <div className="text-red-700 font-medium">
          {error.message}
          {errorRows.length === 0 && ' — no rows were imported.'}
        </div>
      )}
      {errorRows.length > 0 && (
        <table className="mt-2 w-full text-xs">
          <thead className="text-left text-slate-500">
            <tr>
              <th className="py-1 pr-3 w-16">Row</th>
              <th className="py-1 pr-3">Error</th>
            </tr>
          </thead>
          <tbody>
            {errorRows.map((e, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="py-1 pr-3 font-mono">{e.row}</td>
                <td className="py-1 pr-3">{e.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// The API returns row-level errors as ApiError.details = { errors: [...] }
// on a 422 validation failure. Pull them out here.
function extractErrorRows(err: ApiError): ImportError[] | null {
  const d = err.details as { errors?: ImportError[] } | undefined;
  if (d?.errors && Array.isArray(d.errors)) return d.errors;
  return null;
}
